import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tickerMobileUploadAPI } from '../../services/api';

const POLL_MS = 1500;

export default function TickerMobilePhotoModal({
    open,
    onClose,
    gameId,
    minute,
    half,
    onPublished,
}) {
    const [caption, setCaption] = useState('');
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [phase, setPhase] = useState('idle');

    const qrSrc = useMemo(() => {
        if (!session?.uploadUrl) return '';
        return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=12&data=${encodeURIComponent(session.uploadUrl)}`;
    }, [session?.uploadUrl]);

    const startSession = useCallback(async () => {
        if (!gameId) return;
        setLoading(true);
        setError('');
        setPhase('waiting');
        try {
            const res = await tickerMobileUploadAPI.createSession({
                gid: gameId,
                minute: minute || 0,
                half: half || 1,
                caption: caption.trim(),
            });
            setSession(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'QR-Code konnte nicht erstellt werden.');
            setPhase('idle');
        } finally {
            setLoading(false);
        }
    }, [gameId, minute, half, caption]);

    useEffect(() => {
        if (!open) {
            setSession(null);
            setError('');
            setPhase('idle');
            setCaption('');
            return;
        }
        startSession();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || !session?.token || phase !== 'waiting') return undefined;

        let cancelled = false;
        const poll = async () => {
            try {
                const res = await tickerMobileUploadAPI.getSessionStatus(session.token);
                if (cancelled) return;
                if (res.data.status === 'published' && res.data.tickerId) {
                    setPhase('done');
                    onPublished?.({ tickerId: res.data.tickerId, imageUrl: res.data.imageUrl });
                    setTimeout(() => onClose?.(), 1200);
                }
            } catch (_) { /* ignore poll errors */ }
        };

        poll();
        const id = setInterval(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [open, session?.token, phase, onPublished, onClose]);

    if (!open) return null;

    return (
        <div className="ticker-photo-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="ticker-photo-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="ticker-photo-modal-title"
            >
                <div className="ticker-photo-modal-header">
                    <h2 id="ticker-photo-modal-title">📷 Foto vom Handy</h2>
                    <button type="button" className="ticker-photo-modal-close" onClick={onClose} aria-label="Schließen">✕</button>
                </div>

                <p className="ticker-photo-modal-lead">
                    QR-Code mit dem Handy scannen, Foto wählen oder aufnehmen – erscheint sofort im Ticker.
                </p>

                {phase !== 'done' && (
                    <label className="ticker-photo-modal-caption">
                        Optionaler Text (auch am Handy änderbar)
                        <input
                            type="text"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Kurze Bildunterschrift…"
                            disabled={!!session && phase === 'waiting'}
                        />
                    </label>
                )}

                {error && <p className="ticker-photo-modal-error">{error}</p>}

                {loading && <p className="ticker-photo-modal-status">QR-Code wird erstellt…</p>}

                {session?.uploadUrl && phase !== 'done' && (
                    <div className="ticker-photo-modal-qr-wrap">
                        <img src={qrSrc} alt="QR-Code für Handy-Upload" className="ticker-photo-modal-qr" />
                        <p className="ticker-photo-modal-status">
                            {phase === 'waiting' ? 'Warte auf Foto vom Handy…' : ''}
                        </p>
                        <p className="ticker-photo-modal-url">{session.uploadUrl}</p>
                    </div>
                )}

                {phase === 'done' && (
                    <p className="ticker-photo-modal-success">✓ Foto wurde veröffentlicht!</p>
                )}

                <div className="ticker-photo-modal-actions">
                    {session && phase === 'waiting' && (
                        <button type="button" className="ticker-photo-modal-btn" onClick={startSession} disabled={loading}>
                            Neuer QR-Code
                        </button>
                    )}
                    <button type="button" className="ticker-photo-modal-btn ticker-photo-modal-btn--ghost" onClick={onClose}>
                        Schließen
                    </button>
                </div>
            </div>
        </div>
    );
}
